import React, { useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Space } from '@components/atoms/Space';
import { Footer } from '@components/molecules/Footer';
import { MarketplaceAdvertising } from '@components/molecules/MarketplaceAdvertising';
import { StickyHeader } from '@components/molecules/StickyHeader/StickyHeader';
import { useData } from '@hooks/useData';
import { SITE_URLS } from '@constants';
import { Modules, useModule } from '@hooks/useModule';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { Button, ButtonMode } from '@components/atoms/Button';
import { BreadcrumbsBanner } from '@components/molecules/BreadcrumbsBanner';
import { Container } from '@components/atoms/Container';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import { useGetPointsServiceDetail } from '@services/useGetPointsServiceDetail';
import { useGenerateVoucher } from '@services/useGenerateVoucher';
import { useThemes } from '@hooks/useThemes';
import styles from './PointsServiceDetail.module.css';

export function PointsServiceDetail() {
    const location = useLocation();
    const { uuid } = useParams();
    const { navigate } = useCustomNavigate();
    const { useGrayscaleTheme } = useThemes();
    const { isModuleActive } = useModule();
    const { request: requestPointsServiceDetail, loading: loadingPointsServiceDetail } = useGetPointsServiceDetail();
    const {
        request: requestGenerateVoucher,
        loading: loadingGenerateVoucher,
        lastResponseTime: lastGenerateVoucher,
    } = useGenerateVoucher();
    const { login, pointsServiceDetail, allSettings } = useData();
    const { t, i18n } = useTranslation();

    const translations = {
        title: t('points-service-detail-component.title'),
        about: t('points-service-detail-component.about'),
        tradePoints: t('points-service-detail-component.trade-points'),
    };

    // fetch product detail info
    useEffect(() => {
        requestPointsServiceDetail({ uuid: uuid || '' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid, i18n.language, lastGenerateVoucher]);

    // if gamification module is not active, user can't access this page
    useEffect(() => {
        if (allSettings && !isModuleActive(Modules.pointsGamification)) {
            navigate(`/${SITE_URLS.home}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSettings]);

    const handleOnClickTradePoints = () => {
        // if user is not logged in, request for login
        if (login === null) {
            const redirectTo = encodeURIComponent(location.pathname + location.search);
            navigate(`/${SITE_URLS.login}?redirectTo=${redirectTo}`);
        } else {
            requestGenerateVoucher({ service_uuid: pointsServiceDetail?.data?.uuid });
        }
    };

    return (
        <>
            <StickyHeader />

            <>
                {!loadingPointsServiceDetail && (
                    <>
                        <Container>
                            <BreadcrumbsBanner
                                title={translations.title}
                                links={[{ name: translations.title, url: SITE_URLS.pointsServices }]}
                                currentPage={pointsServiceDetail?.data?.name || ''}
                            />
                        </Container>

                        <Space spaceBreakpoints={{ xs: { space: 50 } }} />

                        <Container
                            classes={`px-0 ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`}
                            customSize="custom-large">
                            <>
                                <div className="position-relative">
                                    <>
                                        {pointsServiceDetail?.data?.image?.src ? (
                                            <img
                                                src={pointsServiceDetail?.data?.image?.src}
                                                className={`w-100 ${styles.banner}`}
                                                alt={pointsServiceDetail?.data?.name || ''}
                                            />
                                        ) : (
                                            <div className={styles['placeholder-banner']}></div>
                                        )}

                                        <div className={`${styles.overlay} d-flex justify-content-between`}>
                                            <div className={`${styles.points} align-content-center text-center bg-light`}>
                                                <Typography
                                                    tagVariant={TypographyTagVariants.span}
                                                    fontSize="16"
                                                    color="primary"
                                                    fontWeight="700">
                                                    <>
                                                        <i className="fa-sharp fa-solid fa-trophy"></i>{' '}
                                                        {pointsServiceDetail?.data?.points_needed}
                                                    </>
                                                </Typography>
                                            </div>
                                        </div>
                                    </>
                                </div>
                                <div className="bg-secondary position-relative">
                                    <div className="row py-3 mx-0 align-items-center">
                                        <div className="col-12 col-md-9 py-3 ps-4 pe-3 text-center text-md-start">
                                            <Typography classes="mb-2" appearanceLevel={TypographyAppearanceVariants.level5}>
                                                {pointsServiceDetail?.data?.name || ''}
                                            </Typography>
                                            <Typography classes="text-truncate text-uppercase mb-0" color="primary" fontSize="12">
                                                {pointsServiceDetail?.data?.category}
                                            </Typography>
                                        </div>

                                        <div className="col-12 col-md-3 py-3 px-3 text-center text-md-end">
                                            <Button
                                                mode={ButtonMode.secondary}
                                                label={translations.tradePoints}
                                                onClick={() => handleOnClickTradePoints()}
                                                disabled={
                                                    loadingGenerateVoucher ||
                                                    (login !== null && pointsServiceDetail?.data?.canGetVoucher === false)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        </Container>

                        <Space spaceBreakpoints={{ xs: { space: 50 } }} />

                        <Container classes="px-0" customSize="custom-large">
                            <div className="position-relative">
                                <Typography
                                    classes="mb-3"
                                    tagVariant={TypographyTagVariants.h2}
                                    appearanceLevel={TypographyAppearanceVariants.level3}>
                                    {translations.about}
                                </Typography>

                                <Typography tagVariant={TypographyTagVariants.span}>
                                    <div
                                        className={`${styles.about + `  ${useGrayscaleTheme ? styles['theme-grayscale'] : ''}`} text-muted`}
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(pointsServiceDetail?.data?.description || ''),
                                        }}></div>
                                </Typography>
                            </div>
                        </Container>
                    </>
                )}
            </>
            <Space spaceBreakpoints={{ xs: { space: 50 }, md: { space: 100 } }} />

            <MarketplaceAdvertising />

            <Footer mode="normal" />
        </>
    );
}
